import { useState } from 'react';
import { FiCheck, FiEdit2, FiSettings, FiTrash2, FiClock } from 'react-icons/fi';
import { IoMdSync } from 'react-icons/io';
import { toast } from 'react-toastify';
import { MdOutlineCheckCircle, MdOutlineCancel, MdOutlineArchive, MdOutlinePending, MdOutlineRadioButtonUnchecked, MdUndo } from 'react-icons/md';
import { IoAlertCircle } from 'react-icons/io5';
import PageForm from '../../templates/PageForm';
import DeactivateModal from './shared/DeactivateModal';
import RestoreModal from './shared/RestoreModal';
import OperationLogsDrawer from './shared/OperationLogsDrawer';
import ATable from '../../organisms/ATable';
import Tab, { TabProps } from '../../molecules/Tab';
import CopyButton from '../../atoms/CopyButton';
import NewTooltip from '../../atoms/NewTooltip';
import PageSearch from '../../molecules/PageSearch';
import VerificationConfigSkeleton from './shared/VerificationConfigSkeleton';

import {
    formatCurrency,
    formatDate,
    formatCpfCnpj,
    getIntensityColor,
} from '../../../utils/collateralFormatters';
import { COLLATERAL_BREADCRUMBS } from './collateralUtils';
import useVerificationConfigPageView from '../../../hooks/useVerificationConfigPageView';
import { collateralApi } from '../../../services/collateralApi';
import ProcessCard from './shared/ProcessCard';
import CheckIndicator from './shared/CheckIndicator';
import BatchStatusBadge from './shared/BatchStatusBadge';
import CheckStatusBadge from './shared/CheckStatusBadge';
import DocumentStatusBadge from './shared/DocumentStatusBadge';
import DraweeStatusBadge from './shared/DraweeStatusBadge';
import SummaryCard from './shared/SummaryCard';
import CriteriaConfigPanelEdit from './CriteriaConfigPanelEdit';
import ReceivableItemView from './ReceivableItemView';
import ValidationPanel from './ValidationPanel';

// --- Field display helpers ---
const Field = ({ label, value, color }: { label: string; value: React.ReactNode; color?: string }) => (
    <div className="flex justify-between items-center py-0.5">
        <span className="text-gray-500 dark:text-gray-400 text-xs">{label}</span>
        {color ? (
            <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${color}`}>{value}</span>
        ) : (
            <span className="text-xs dark:text-white text-right max-w-[60%] truncate" title={String(value ?? '')}>{value != null && value !== '' ? value : '—'}</span>
        )}
    </div>
);

const SectionTitle = ({ children, color }: { children: string; color?: string }) => (
    <h3 className={`text-[10px] font-semibold uppercase mb-2 tracking-wide ${color || 'text-gray-500 dark:text-gray-400'}`}>{children}</h3>
);

// --- Process validation icons ---
const INTENSITY_LABELS: Record<string, string> = { BAIXA: 'Baixa', MEDIA: 'Média', ALTA: 'Alta', CAOTICA: 'Caótica' };
const ProcessIcon = ({ received, status }: { received?: boolean; status?: string }) => {
    if (!received) return <MdOutlineRadioButtonUnchecked className="text-gray-300" size={14} title="Não recebido" />;
    switch (status) {
        case 'VALIDATED': return <MdOutlineCheckCircle className="text-green-500" size={14} title="Validado" />;
        case 'REJECTED': return <MdOutlineCancel className="text-red-500" size={14} title="Rejeitado" />;
        case 'ARCHIVED': return <MdOutlineArchive className="text-blue-500" size={14} title="Arquivado" />;
        default: return <MdOutlinePending className="text-amber-500" size={14} title="Pendente" />;
    }
};

// --- Constants ---
const PROCESS_LABELS: Record<string, string> = {
    XML: 'XML', DRAWEE: 'Sacado', INVOICE_PDF: 'NF PDF',
    PURCHASE_CONTRACT: 'Contrato C/V', RECEIPT_STUB: 'Canhoto',
    ACCEPTED_BILL: 'Duplicata',
};
const ALL_PROCESSES = ['XML', 'INVOICE_PDF', 'RECEIPT_STUB', 'ACCEPTED_BILL', 'PURCHASE_CONTRACT'];
const TAB_ITEMS: TabProps[] = [
    { id: 'general', title: 'Geral', index: 0 },
    { id: 'metrics', title: 'Métricas e checagem', index: 1 },
    { id: 'receivables', title: 'Recebíveis', index: 2 },
];

// --- Main Component ---
const VerificationConfigPageView = () => {
    const {
        id, navigate, isLoading, data, tabIndex, setTabIndex,
        titles, enabledProcesses, summary,
        selectedIds, setSelectedIds, editingIds,
        showValidationPanel, setShowValidationPanel,
        batchProcess, setBatchProcess, batchStatus, setBatchStatus,
        batchObservation, setBatchObservation, isSaving,
        sortField, setSortField, sortOrder, setSortOrder,
        searchTerm, setSearchTerm,
        openBatchPanel, openEditPanel, toggleSelect, handleSaveValidation, refresh,
        syncReceivables, isSyncing,
    } = useVerificationConfigPageView();

    const [showCriteriaPanel, setShowCriteriaPanel] = useState(false);
    const [viewItemId, setViewItemId] = useState<string | null>(null);
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [isDeactivating, setIsDeactivating] = useState(false);
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [isRestoring, setIsRestoring] = useState(false);
    const [showLogsDrawer, setShowLogsDrawer] = useState(false);
    const viewItem = viewItemId ? (data?.titles || []).find((t: any) => t.id === viewItemId) : null;

    const handleDeactivate = async (reason: string) => {
        if (!id) return;
        setIsDeactivating(true);
        try {
            await collateralApi.deactivateVerificationConfig(id, reason);
            toast.success('Verificação desativada com sucesso');
            setShowDeactivateModal(false);
            navigate('/collateral/verification-config');
        } catch {
            toast.error('Erro ao desativar verificação');
        } finally {
            setIsDeactivating(false);
        }
    };

    const handleRestore = async (reason: string) => {
        if (!id) return;
        setIsRestoring(true);
        try {
            await collateralApi.restoreVerificationConfig(id, reason);
            toast.success('Verificação restaurada com sucesso');
            setShowRestoreModal(false);
            refresh();
        } catch {
            toast.error('Erro ao restaurar verificação');
        } finally {
            setIsRestoring(false);
        }
    };

    if (isLoading) return <VerificationConfigSkeleton />;
    if (!data) return <div className="p-8 text-red-600">Garantia não encontrada</div>;

    return (
        <PageForm
            pageTitle={`Verificação — ${data.operationCode || ''}`}
            apiVersion={data.apiVersion ? `${data.apiName} - ${data.apiVersion} (${(data.environment || '').toUpperCase()})` : ''}
            isLoading={false}
            breadcrumb={[
                COLLATERAL_BREADCRUMBS.verificationConfig,
                { label: (data.operationCode || '').substring(0, 10), url: '#' },
            ]}
            controlSlot={
                <div className="flex justify-end items-center space-x-2">
                    <button onClick={() => setShowLogsDrawer(true)}
                        className="bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 px-2.5 rounded text-xs flex items-center space-x-1.5 h-[30px]"
                        title="Histórico de operações">
                        <FiClock size={13} /> <span>Histórico</span>
                    </button>
                    {data.active && (
                        <>
                            <button type="button" onClick={syncReceivables} disabled={isSyncing}
                                className="bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 px-2.5 rounded text-xs flex items-center space-x-1.5 h-[30px] disabled:opacity-50 disabled:cursor-not-allowed">
                                <IoMdSync size={14} className={isSyncing ? 'animate-spin' : ''} /> <span>Sincronizar</span>
                            </button>
                            <button onClick={() => setShowCriteriaPanel(true)}
                                className="bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 px-2.5 rounded text-xs flex items-center space-x-1.5 h-[30px]">
                                <FiSettings size={13} /> <span>Configuração</span>
                            </button>
                            <button onClick={() => navigate(`/collateral/verification-config/${id}/view/edit`, { state: { data } })}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-2.5 rounded text-xs flex items-center space-x-1.5 h-[30px]">
                                <FiEdit2 size={13} /> <span>Editar</span>
                            </button>
                            <button onClick={() => setShowDeactivateModal(true)}
                                className="bg-red-500 hover:bg-red-600 text-white px-2.5 rounded text-xs flex items-center space-x-1.5 h-[30px]"
                                title="Desativar verificação">
                                <FiTrash2 size={13} /> <span>Desativar</span>
                            </button>
                            <button onClick={() => openBatchPanel()} disabled={selectedIds.size === 0}
                                className="px-2.5 rounded text-xs flex items-center space-x-1.5 h-[30px] bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-300 disabled:dark:bg-slate-600 disabled:text-gray-500 disabled:dark:text-gray-400 disabled:cursor-not-allowed">
                                <FiCheck size={13} /> <span>Validar ({selectedIds.size})</span>
                            </button>
                        </>
                    )}
                    {!data.active && (
                        <button onClick={() => setShowRestoreModal(true)}
                            className="bg-green-600 hover:bg-green-700 text-white px-2.5 rounded text-xs flex items-center space-x-1.5 h-[30px]">
                            <MdUndo size={14} /> <span>Restaurar</span>
                        </button>
                    )}
                </div>
            }
        >
            {!data.active && (
                <div className="-mt-2 mb-2">
                    <span className="text-xs text-red-500 font-medium px-2 py-1 bg-red-50 dark:bg-red-900/20 rounded">Desativada</span>
                </div>
            )}
            {!data.active && data.deactivationReason && (
                <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg">
                    <span className="text-[10px] font-semibold uppercase text-red-600 dark:text-red-400">Motivo da desativação</span>
                    <div className="mt-1 text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{data.deactivationReason.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim()}</div>
                </div>
            )}
            <div className="mt-4">
                <Tab tabItems={TAB_ITEMS} tabIndex={tabIndex} setTabIndex={setTabIndex} />

                {tabIndex === 0 && <GeneralTab data={data} summary={summary} enabledProcesses={enabledProcesses} isActive={data.active !== false} />}
                {tabIndex === 1 && <MetricsTab data={data} enabledProcesses={enabledProcesses} />}
                {tabIndex === 2 && (
                    <ReceivablesTab
                        titles={titles} enabledProcesses={enabledProcesses}
                        selectedIds={selectedIds} setSelectedIds={setSelectedIds}
                        sortField={sortField} setSortField={setSortField}
                        sortOrder={sortOrder} setSortOrder={setSortOrder}
                        searchTerm={searchTerm} setSearchTerm={setSearchTerm}
                        toggleSelect={toggleSelect} openEditPanel={openEditPanel}
                        setViewItemId={setViewItemId}
                        isActive={data.active !== false}
                    />
                )}
            </div>

            {showValidationPanel && (
                <ValidationPanel
                    titles={titles} enabledProcesses={enabledProcesses}
                    editingIds={editingIds} selectedIds={selectedIds}
                    initialProcess={batchProcess}
                    initialStatus={batchStatus}
                    initialObservation={batchObservation}
                    isSaving={isSaving}
                    onClose={() => setShowValidationPanel(false)}
                    onSave={handleSaveValidation}
                />
            )}

            {showCriteriaPanel && data && (
                <CriteriaConfigPanelEdit
                    configUuid={data.id}
                    validationConfig={data.validationConfig || {}}
                    operationSummary={{ operationCode: data.operationCode || '', assignorName: data.assignor || '', totalAmount: data.totalAmount || 0, totalReceivables: data.totalTitles || 0 }}
                    operationType={data.operationType || ''}
                    obligationType={data.obligationType || ''}
                    traderName={data.traderName || ''}
                    analysisCriteria={data.analysisMode === 'SAMPLE' ? 'Amostragem' : 'Completa'}
                    sampledTitles={data.sampledTitles || 1}
                    hasDraweeInProgress={data.validationStatus === 'PARTIALLY_VALIDATED' || data.validationStatus === 'IN_PROGRESS'}
                    onClose={() => setShowCriteriaPanel(false)}
                    onSaved={() => { setShowCriteriaPanel(false); refresh(); }}
                />
            )}

            {viewItem && (
                <ReceivableItemView item={viewItem} onClose={() => setViewItemId(null)} />
            )}

            <DeactivateModal
                isOpen={showDeactivateModal}
                operationCode={data.operationCode || ''}
                loading={isDeactivating}
                onCancel={() => setShowDeactivateModal(false)}
                onConfirm={handleDeactivate}
            />

            <RestoreModal
                isOpen={showRestoreModal}
                operationCode={data.operationCode || ''}
                loading={isRestoring}
                onCancel={() => setShowRestoreModal(false)}
                onConfirm={handleRestore}
            />

            <OperationLogsDrawer
                isOpen={showLogsDrawer}
                logs={data.operationLogs || []}
                onClose={() => setShowLogsDrawer(false)}
            />
        </PageForm>
    );
};

export default VerificationConfigPageView;

// --- Tab: Geral ---
const GeneralTab = ({ data, summary, enabledProcesses, isActive }: { data: any; summary: any; enabledProcesses: string[]; isActive: boolean }) => {
    const isDraweeEnabled = enabledProcesses.includes('DRAWEE');
    return (
    <div className="mt-4 space-y-4">
        {/* Row 1: Obrigação + Configuração */}
        <div className="grid grid-cols-2 gap-4">
            {/* Card Obrigação */}
            <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                <SectionTitle color="text-blue-700 dark:text-blue-400">Obrigação</SectionTitle>
                <div className="space-y-0.5">
                    <Field label="Nº da operação" value={data.operationNumber} />
                    <Field label="Data emissão" value={formatDate(data.issueDate)} />
                    <Field label="Tipo operação" value={data.operationType} />
                    <Field label="Obrigação" value={data.obligation || '—'} />
                    <Field label="Cedente" value={data.assignor} />
                    <Field label="CNPJ Cedente" value={formatCpfCnpj(data.assignorTaxId)} />
                    <Field label="Nº de títulos" value={data.totalTitles} />
                    <Field label="Valor total" value={formatCurrency(data.totalAmount || 0)} />
                </div>
            </div>

            {/* Card Configuração */}
            <div className="bg-green-50 dark:bg-green-900/10 rounded-lg p-4 border border-green-200 dark:border-green-800">
                <div className="flex justify-between items-center mb-2">
                    <SectionTitle color="text-green-700 dark:text-green-400">Configuração</SectionTitle>
                </div>
                <div className="space-y-0.5">
                    <Field label="Trader" value={data.traderName} />
                    <Field label="Intensidade" value={(INTENSITY_LABELS[data.analysisIntensity] || data.analysisIntensity)} color={data.analysisIntensity ? getIntensityColor(data.analysisIntensity) : undefined} />
                    <Field label="Critério análise" value={data.analysisCriteria || '—'} />
                    {data.sampledTitles && <Field label="Títulos amostragem" value={`${data.sampledTitles} (${data.totalTitles > 0 ? Math.round(data.sampledTitles / data.totalTitles * 100) : 0}%)`} />}
                    <div className="flex justify-between items-center py-0.5">
                        <span className="text-gray-500 dark:text-gray-400 text-xs">Status documental</span>
                        <BatchStatusBadge status={data.guaranteeStatus} statusFormatted={data.guaranteeStatusFormatted || data.guaranteeStatus} />
                    </div>
                    {isDraweeEnabled && (
                        <div className="flex justify-between items-center py-0.5">
                            <span className="text-gray-500 dark:text-gray-400 text-xs">Status validação</span>
                            <BatchStatusBadge status={data.validationStatus} statusFormatted={data.validationStatusFormatted || data.validationStatus} />
                        </div>
                    )}
                </div>
            </div>
        </div>

        {/* Row 2: Card Análise */}
        <div className="bg-amber-50 dark:bg-amber-900/10 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
            <SectionTitle color="text-amber-700 dark:text-amber-400">Análise</SectionTitle>
            <div className="space-y-3">
                <div>
                    <span className="text-gray-500 dark:text-gray-400 text-xs">Responsável</span>
                    <div className="mt-1 text-xs dark:text-white bg-white dark:bg-slate-800 rounded p-2 min-h-[40px] border border-amber-200 dark:border-slate-700">{data.analystName || '—'}</div>
                </div>
                <div>
                    <span className="text-gray-500 dark:text-gray-400 text-xs">Pendência</span>
                    <div className="mt-1 text-xs dark:text-white bg-white dark:bg-slate-800 rounded p-2 min-h-[40px] border border-amber-200 dark:border-slate-700 prose prose-xs max-w-none" dangerouslySetInnerHTML={{ __html: data.pendency || '<span class="text-gray-400">—</span>' }} />
                </div>
                <div>
                    <span className="text-gray-500 dark:text-gray-400 text-xs">Observação</span>
                    <div className="mt-1 text-xs dark:text-white bg-white dark:bg-slate-800 rounded p-2 min-h-[40px] border border-amber-200 dark:border-slate-700 prose prose-xs max-w-none" dangerouslySetInnerHTML={{ __html: data.observation || '<span class="text-gray-400">—</span>' }} />
                </div>
            </div>
        </div>

        {/* Row 3: Controles booleanos */}
        <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded">
                <Field label="Lastro salvo em garantias?" value={data.collateralSaved ? 'SIM' : 'NÃO'} />
            </div>
            <div className="p-3 bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded space-y-2">
                <Field label="Registro em cartório" value={data.courtRegistration ? 'SIM' : 'NÃO'} />
                <Field label="Qual contrato registrar?" value={data.contractToRegister} />
            </div>
            {data.debtorNotificationDone != null && (
                <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded">
                    <Field label="Cedente notificou os sacados?" value={data.debtorNotificationDone ? 'SIM' : 'NÃO'} />
                </div>
            )}
        </div>
    </div>
);
}

// --- Tab: Métricas e checagem ---
const MetricsTab = ({ data, enabledProcesses }: { data: any; enabledProcesses: string[] }) => {
    const NON_DRAWEE_PROCESSES = ALL_PROCESSES.filter(p => p !== 'DRAWEE');
    const draweeData = data.processValidations?.find((pv: any) => pv.processType === 'DRAWEE') || {};
    const isDraweeEnabled = enabledProcesses.includes('DRAWEE');

    const allTitles = data.titles || [];
    const docTotal = allTitles.length;
    const docOk = allTitles.filter((t: any) => t.status === 'OK').length;
    const docNotOk = allTitles.filter((t: any) => t.status === 'NOT_OK').length;
    const docPending = docTotal - docOk - docNotOk;
    const pctDocOk = docTotal > 0 ? (docOk / docTotal) * 100 : 0;
    const pctDocNotOk = docTotal > 0 ? (docNotOk / docTotal) * 100 : 0;
    const pctDocPending = docTotal > 0 ? (docPending / docTotal) * 100 : 0;

    return (
        <div className="mt-4 space-y-4">
            <div className="grid grid-cols-4 gap-3">
                {NON_DRAWEE_PROCESSES.slice(0, 4).map(p => {
                    const d = data.processValidations?.find((pv: any) => pv.processType === p) || {};
                    return <ProcessCard key={p} label={PROCESS_LABELS[p]} isEnabled={enabledProcesses.includes(p)} status={d.status} statusFormatted={d.statusFormatted} receivedCount={d.receivedCount} pendingCount={d.pendingCount} validatedCount={d.validatedCount} />;
                })}
            </div>
            <div className={`grid ${isDraweeEnabled ? 'grid-cols-[1fr_3fr]' : 'grid-cols-1'} gap-3`}>
                {NON_DRAWEE_PROCESSES.slice(4).map(p => {
                    const d = data.processValidations?.find((pv: any) => pv.processType === p) || {};
                    return <ProcessCard key={p} label={PROCESS_LABELS[p]} isEnabled={enabledProcesses.includes(p)} status={d.status} statusFormatted={d.statusFormatted} receivedCount={d.receivedCount} pendingCount={d.pendingCount} validatedCount={d.validatedCount} />;
                })}
                {isDraweeEnabled && (
                    <div className="rounded p-3 border-2 bg-white dark:bg-slate-800 border-blue-200 dark:border-blue-700 shadow-sm">
                        <span className="text-[10px] font-semibold uppercase text-gray-600 dark:text-gray-300">Validação de sacado</span>
                        <div className="mt-2 grid grid-cols-3 gap-2">
                            <SummaryCard label="Sacados" total={data.draweeSummary?.totalDebtors ?? 0} validated={data.draweeSummary?.validatedDebtors ?? 0} />
                            <SummaryCard label="Títulos" total={data.draweeSummary?.totalTitles ?? 0} validated={data.draweeSummary?.validatedTitles ?? 0} />
                            <SummaryCard label="Valor Total" total={data.draweeSummary?.totalAmount ?? 0} validated={data.draweeSummary?.validatedAmount ?? 0} isCurrency />
                        </div>
                    </div>
                )}
            </div>

            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase">Progresso do Lote</span>
                <div className="mt-3 space-y-3">
                    {ALL_PROCESSES.filter(p => enabledProcesses.includes(p)).map(p => {
                        const d = data.processValidations?.find((pv: any) => pv.processType === p) || {};
                        const total = d.totalCount ?? data.totalTitles ?? 0;
                        const validated = d.validatedCount ?? 0;
                        const pending = d.pendingCount ?? 0;
                        const rejected = total - validated - pending;
                        const pctValidated = total > 0 ? (validated / total) * 100 : 0;
                        const pctPending = total > 0 ? (pending / total) * 100 : 0;
                        const pctRejected = total > 0 ? (Math.max(0, rejected) / total) * 100 : 0;
                        return (
                            <div key={p}>
                                <div className="flex justify-between text-xs mb-1">
                                    <span className="text-gray-600 dark:text-gray-300">{PROCESS_LABELS[p]}</span>
                                    <span className="text-gray-500">{validated}/{total}</span>
                                </div>
                                <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2 flex overflow-hidden">
                                    {pctValidated > 0 && <div className="bg-green-500 h-2" style={{ width: `${pctValidated}%` }} />}
                                    {pctPending > 0 && <div className="bg-amber-400 h-2" style={{ width: `${pctPending}%` }} />}
                                    {pctRejected > 0 && <div className="bg-red-500 h-2" style={{ width: `${pctRejected}%` }} />}
                                </div>
                            </div>
                        );
                    })}
                    {/* Status do Recebível (document_status) */}
                    <div>
                        <div className="flex justify-between text-xs mb-1">
                            <span className="text-gray-600 dark:text-gray-300 font-medium">Status Recebível</span>
                            <span className="text-gray-500">{docOk}/{docTotal}</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2 flex overflow-hidden">
                            {pctDocOk > 0 && <div className="bg-green-500 h-2" style={{ width: `${pctDocOk}%` }} />}
                            {pctDocPending > 0 && <div className="bg-amber-400 h-2" style={{ width: `${pctDocPending}%` }} />}
                            {pctDocNotOk > 0 && <div className="bg-red-500 h-2" style={{ width: `${pctDocNotOk}%` }} />}
                        </div>
                    </div>
                </div>
                <div className="border-t border-gray-200 dark:border-slate-700 mt-4 pt-3 space-y-2">
                    <div className="flex justify-between text-xs"><span className="text-gray-600 dark:text-gray-300 font-medium">Total de títulos</span><span className="dark:text-white font-medium">{data.totalTitles}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-gray-600 dark:text-gray-300 font-medium">Valor total</span><span className="dark:text-white font-medium">{formatCurrency(data.totalAmount || 0)}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-gray-600 dark:text-gray-300 font-medium">% validado</span><span className="dark:text-white font-medium">{data.validatedPercentage || 0}%</span></div>
                </div>
                <div className="border-t border-gray-200 dark:border-slate-700 mt-3 pt-3">
                    <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-600 dark:text-gray-300 font-medium">Intensidade</span>
                        {data.analysisIntensity && <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getIntensityColor(data.analysisIntensity)}`}>{(INTENSITY_LABELS[data.analysisIntensity] || data.analysisIntensity)}</span>}
                    </div>
                    <div className="flex justify-between text-xs mt-1"><span className="text-gray-500">Critério</span><span className="dark:text-white">{data.analysisCriteria || '—'}</span></div>
                    {data.sampledTitles && <div className="flex justify-between text-xs mt-1"><span className="text-gray-500">Títulos amostragem</span><span className="dark:text-white">{data.sampledTitles}</span></div>}
                </div>
            </div>
        </div>
    );
};

// --- Tab: Recebíveis ---
const ReceivablesTab = ({ titles, enabledProcesses, selectedIds, setSelectedIds, sortField, setSortField, sortOrder, setSortOrder, searchTerm, setSearchTerm, toggleSelect, openEditPanel, setViewItemId, isActive = true }: {
    titles: any[]; enabledProcesses: string[];
    selectedIds: Set<string>; setSelectedIds: (s: Set<string>) => void;
    sortField: string; setSortField: (f: string) => void;
    sortOrder: 'ASC' | 'DESC'; setSortOrder: (o: 'ASC' | 'DESC') => void;
    searchTerm: string; setSearchTerm: (s: string) => void;
    toggleSelect: (id: string) => void; openEditPanel: (id: string) => void;
    setViewItemId: (id: string | null) => void;
    isActive?: boolean;
}) => (
    <div className="mt-3 overflow-hidden">
        <PageSearch
            value={searchTerm}
            label="Buscar por NF, NFe, sacado ou CPF/CNPJ..."
            onSearch={setSearchTerm}
            onChange={setSearchTerm}
            filterCleanVisible={true}
            filterCleanEnabled={!!searchTerm}
            onFilterClean={() => setSearchTerm('')}
            configureVisible={false}
        />
        <div className="overflow-auto border border-gray-200 dark:border-slate-700" style={{ maxHeight: 'calc(100vh - 210px)' }}>
            <ATable
                headers={[
                    { id: 'titleNumber', title: 'Título', field: 'titleNumber', canSort: true, width: '60px' },
                    { id: 'debtorName', title: 'Sacado', field: 'debtorName', canSort: true },
                    { id: 'city', title: 'Município - UF', field: 'city', canSort: true, width: '220px' },
                    { id: 'receivableNumber', title: 'nDup', field: 'receivableNumber', canSort: true, width: '45px' },
                    { id: 'amount', title: 'vDup', field: 'amount', canSort: true, width: '90px', cssClass: 'text-right' },
                    { id: 'dueDate', title: 'Venc.', field: 'dueDate', canSort: true, width: '80px' },
                    ...ALL_PROCESSES.map(p => ({
                        id: `proc_${p}`, title: PROCESS_LABELS[p] || p, field: `proc_${p}`,
                        canSort: false, width: '55px',
                        cssClass: `text-center ${!enabledProcesses.includes(p) ? 'text-gray-300 dark:text-gray-600' : ''}`,
                    })),
                    { id: 'status', title: 'Status', field: 'status', canSort: true, width: '120px', cssClass: 'text-center' },
                    { id: 'actions', title: '', field: 'actions', canSort: false, width: '50px', cssClass: 'text-center' },
                ]}
                fieldSorted={sortField}
                orderSorted={sortOrder}
                onSort={(field, order) => { setSortField(field); setSortOrder(order); }}
                canSelectAll={isActive}
                onSelect={(checked: boolean) => {
                    setSelectedIds(checked ? new Set(titles.filter((t: any) => t.canValidate).map((t: any) => t.id)) : new Set());
                }}
            >
                {titles.map((row: any, index: number) => {
                    const processItems: Record<string, any> = {};
                    (row.processItems || []).forEach((pi: any) => { processItems[pi.processType] = pi; });
                    return (
                        <tr key={row.id} role="button" tabIndex={0} onClick={() => setViewItemId(row.id)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setViewItemId(row.id); } }} className={`group hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}`}>
                            <td className="py-2 px-1 border-b border-gray-200 dark:border-slate-700 text-center" onClick={e => e.stopPropagation()}>
                                {isActive && row.canValidate && (
                                    <input type="checkbox" checked={selectedIds.has(row.id)} onChange={() => toggleSelect(row.id)}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500" />
                                )}
                            </td>
                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white">{row.titleNumber}</td>
                            <td className="py-1 px-3 border-b border-gray-200 dark:border-slate-700">
                                <div className="text-xs dark:text-white truncate" title={row.debtorName}>{row.debtorName}</div>
                                <div className="text-[11px] text-gray-400 flex items-center gap-1">
                                    <span>{formatCpfCnpj(row.debtorDocument)}</span>
                                    <CopyButton data={row.debtorDocument} label="Copiar documento" className="p-0 hover:bg-transparent" />
                                    <span className="mx-2">·</span>
                                    <span title={row.nfeKey}>{row.nfeKey}</span>
                                    <CopyButton data={row.nfeKey} label="Copiar NFe" className="p-0 hover:bg-transparent" />
                                </div>
                            </td>
                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white">{row.city}{row.state ? ` - ${row.state}` : ''}</td>
                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white">{row.receivableNumber || '—'}</td>
                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white text-right">{formatCurrency(row.amount)}</td>
                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white">{formatDate(row.dueDate)}</td>
                            {ALL_PROCESSES.map(p => {
                                const pi = processItems[p];
                                const isEnabled = enabledProcesses.includes(p);
                                return (
                                    <td key={p} className="py-2 px-1 border-b border-gray-200 dark:border-slate-700 text-center">
                                        {isEnabled ? (
                                            <div className="flex items-center justify-center gap-0.5">
                                                <CheckIndicator received={!!pi?.received} status={pi?.status} />
                                                {pi?.observation && <NewTooltip content={pi.observation} alignment="bottom"><IoAlertCircle size={10} className="text-amber-500" /></NewTooltip>}
                                            </div>
                                        ) : (
                                            <span className="text-gray-200 dark:text-gray-700">—</span>
                                        )}
                                    </td>
                                );
                            })}
                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs text-center">
                                <div className="flex flex-col items-center gap-0.5">
                                    <div className="flex items-center gap-1">
                                        <DocumentStatusBadge status={row.status || ''} statusFormatted={row.statusFormatted || row.status || ''} />
                                        {row.observation && <NewTooltip content={row.observation} alignment="bottom"><IoAlertCircle size={12} className="text-amber-500" /></NewTooltip>}
                                    </div>
                                    {enabledProcesses.includes('DRAWEE') && row.draweeStatus && <span title="Status do Sacado"><DraweeStatusBadge status={row.draweeStatus} statusFormatted={row.draweeStatusFormatted || row.draweeStatus} /></span>}
                                </div>
                            </td>
                            <td className="py-2 px-1 border-b border-gray-200 dark:border-slate-700 text-center" onClick={e => e.stopPropagation()}>
                                {isActive && (
                                    <button onClick={() => openEditPanel(row.id)} title="Editar"
                                        className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-500 hover:text-blue-700">
                                        <FiEdit2 size={12} />
                                    </button>
                                )}
                            </td>
                        </tr>
                    );
                })}
            </ATable>
        </div>
    </div>
);


